import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  message,
  notification,
  Button,
  Modal,
} from 'antd';
import { withRouter } from 'react-router-dom';

import GeneratePanel from '../../components/generator/GeneratePanel';
import PreviewPanel from '../../components/preview/PreviewPanel';
import { aiApi, accountApi, characterApi } from '../../modules/api';
import { mapSubscriptionFromApi } from '../../utils/subscription';
import './ai.css';

const HISTORY_PAGE_SIZE = 12;
const DEFAULT_GENERATION_MODEL = 'qwen-image-3-0-pro';
const HISTORY_RECOVERY_ATTEMPTS = 36;
const HISTORY_RECOVERY_DELAY_MS = 5000;
const HISTORY_RECOVERY_MATCH_WINDOW_MS = 5 * 60 * 1000;
const GENERATION_STATUS_FAST_POLL_ATTEMPTS = 12;
const GENERATION_STATUS_FAST_POLL_MS = 5000;
const GENERATION_STATUS_SLOW_POLL_MS = 10000;
const GENERATION_STATUS_MAX_POLLS = 60;
const PENDING_GENERATION_JOBS_KEY = 'asi:pending-generation-jobs';

const wait = (durationMs) => new Promise((resolve) => {
  const setTimer = typeof window !== 'undefined' ? window.setTimeout : setTimeout;
  setTimer(resolve, durationMs);
});

const normalizeComparableText = (value) =>
  String(value || '').trim().toLowerCase();

const readPendingGenerationJobs = (userId) => {
  if (typeof window === 'undefined' || !window.localStorage || !userId) {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(PENDING_GENERATION_JOBS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((job) => job && job.userId === userId && job.requestId)
      : [];
  } catch (error) {
    return [];
  }
};

const writePendingGenerationJobs = (jobs) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  window.localStorage.setItem(PENDING_GENERATION_JOBS_KEY, JSON.stringify(jobs));
};

const savePendingGenerationJob = (job) => {
  if (!job || !job.userId || !job.requestId) {
    return;
  }

  const jobs = readPendingGenerationJobs(job.userId);
  const nextJobs = [
    ...jobs.filter((item) => item.requestId !== job.requestId),
    {
      ...job,
      createdAt: job.createdAt || new Date().toISOString(),
    },
  ].slice(-5);
  writePendingGenerationJobs(nextJobs);
};

const removePendingGenerationJob = (userId, requestId) => {
  if (!userId || !requestId) {
    return;
  }

  const jobs = readPendingGenerationJobs(userId)
    .filter((job) => job.requestId !== requestId);
  writePendingGenerationJobs(jobs);
};

const getCharacterPreviewAsset = (character) => {
  if (!character) {
    return null;
  }

  const candidates = [
    character.coverAvatar,
    ...(Array.isArray(character.avatarImages) ? character.avatarImages : []),
    ...(Array.isArray(character.runtimeReferenceImages) ? character.runtimeReferenceImages : []),
    ...(Array.isArray(character.uploadedSourceImages) ? character.uploadedSourceImages : []),
  ];

  return candidates.find((asset) => asset && asset.url) || null;
};

const formatApiErrorDetails = (errors) => {
  if (!errors) {
    return '';
  }

  if (Array.isArray(errors)) {
    return errors
      .map((entry) => {
        if (!entry) {
          return '';
        }
        if (typeof entry === 'string') {
          return entry;
        }
        if (entry.message) {
          return entry.message;
        }
        return JSON.stringify(entry);
      })
      .filter(Boolean)
      .join(' ');
  }

  if (typeof errors === 'string') {
    return errors;
  }

  try {
    return JSON.stringify(errors);
  } catch (error) {
    return String(errors);
  }
};

const extractApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error && error.response && error.response.data;
  const responseMessage =
    (responseData && (responseData.message || responseData.error)) || '';
  const responseDetails = formatApiErrorDetails(responseData && responseData.errors);
  const requestBody =
    error &&
    error.request &&
    typeof error.request.responseText === 'string'
      ? error.request.responseText.trim()
      : '';

  const combinedMessage = [responseMessage, responseDetails]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (combinedMessage) {
    return combinedMessage;
  }

  if (requestBody) {
    try {
      const parsed = JSON.parse(requestBody);
      const parsedMessage = [parsed && parsed.message, formatApiErrorDetails(parsed && parsed.errors)]
        .filter(Boolean)
        .join(' ')
        .trim();
      if (parsedMessage) {
        return parsedMessage;
      }
    } catch (parseError) {
      return requestBody;
    }
  }

  if (error && error.message && error.message !== 'Network Error') {
    return error.message;
  }

  return fallbackMessage;
};

const showGenerationErrorNotification = (title, description) => {
  notification.error({
    message: title,
    description,
    duration: 7,
    placement: 'topRight',
  });
};

const isSensitiveContentMessage = (value) => {
  const normalized = String(value || '').toLowerCase();
  return normalized.includes('sensitive information')
    || normalized.includes('sensitive content')
    || normalized.includes('sensitivecontent')
    || normalized.includes('outputimagesensitivecontentdetected');
};

const sanitizeUiErrorMessage = (value, fallbackMessage) => {
  const raw = String(value || '').trim();
  if (!raw) {
    return fallbackMessage;
  }

  const normalized = raw.toLowerCase();
  if (isSensitiveContentMessage(normalized)) {
    return 'The safety system blocked this request because it may contain sensitive content. If you used reference images, this may be caused by a public person, brand, logo, or protected likeness. If this was text-only, revise sensitive wording. You can also try another supported model.';
  }

  if (normalized.includes('timeout') || normalized.includes('timed out')) {
    return 'The provider did not finish in time after an automatic retry. Please try again, reduce reference inputs, or choose another supported model.';
  }

  const withoutProvider = raw
    .replace(/byteplus[^:]*:\s*/ig, '')
    .replace(/\s*request id:\s*[\w-]+/ig, '')
    .trim();

  if (/byteplus|request id/i.test(withoutProvider) || !withoutProvider) {
    return fallbackMessage;
  }

  return withoutProvider;
};

const getGenerationErrorTitle = (message, fallbackTitle) => (
  isSensitiveContentMessage(message) ? 'Request blocked by safety filter' : fallbackTitle
);

const AI = ({ history, location, user }) => {
  const [, setPromptHistory] = useState([]);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [promptSelectionVersion, setPromptSelectionVersion] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [latestGeneration, setLatestGeneration] = useState(null);
  const [generationError, setGenerationError] = useState(null);
  const [selectedModel, setSelectedModel] = useState(DEFAULT_GENERATION_MODEL);
  const [selectedSize, setSelectedSize] = useState('1024x1024');
  const [generationModels, setGenerationModels] = useState([]);
  const [historyReferenceImages, setHistoryReferenceImages] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [activateCharacterSwap, setActivateCharacterSwap] = useState(false);
  const [characterPickerMode, setCharacterPickerMode] = useState('swap');
  const [characterPickerOpen, setCharacterPickerOpen] = useState(false);
  const [characterReviewOpen, setCharacterReviewOpen] = useState(false);
  const [characterList, setCharacterList] = useState([]);
  const [isCharacterListLoading, setIsCharacterListLoading] = useState(false);
  const [characterListError, setCharacterListError] = useState(null);
  const [characterReviewLoading, setCharacterReviewLoading] = useState(false);
  const isMountedRef = useRef(true);
  const pendingPollsRef = useRef(new Set());
  const currentUserId = user && user.id ? String(user.id) : null;
  const quotaLimit =
    subscription && Number.isFinite(subscription.quotaLimit)
      ? subscription.quotaLimit
      : null;
  const quotaUsed = subscription ? Number(subscription.quotaUsed || 0) : 0;
  const quotaRemaining =
    subscription && subscription.quotaRemaining != null
      ? Number(subscription.quotaRemaining)
      : quotaLimit != null
      ? Math.max(quotaLimit - quotaUsed, 0)
      : null;
  const quotaUsagePercent =
    quotaLimit != null && quotaLimit > 0
      ? Math.min(100, Math.round((quotaUsed / quotaLimit) * 100))
      : null;
  const quotaRemainingPercent =
    quotaUsagePercent != null ? Math.max(0, 100 - quotaUsagePercent) : null;

  useEffect(() => {
    let isCurrent = true;

    const loadGenerationModels = async () => {
      try {
        const { data } = await aiApi.fetchGenerationModels();
        if (!isCurrent) {
          return;
        }
        setGenerationModels(Array.isArray(data) ? data : []);
      } catch (error) {
        if (isCurrent) {
          setGenerationModels([]);
        }
      }
    };

    loadGenerationModels();

    return () => {
      isCurrent = false;
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const normalizeHistoryEntry = useCallback((entry) => {
    if (!entry) {
      return null;
    }

    const resolvedId =
      entry.id ||
      entry._id ||
      (entry.createdAt ? `local-${entry.createdAt}` : `local-${Date.now()}`);

    const createdAtDate = entry.createdAt ? new Date(entry.createdAt) : null;
    const createdAtValue = createdAtDate && !Number.isNaN(createdAtDate.getTime())
      ? createdAtDate.getTime()
      : Date.now();
    const createdAtISO =
      createdAtDate && !Number.isNaN(createdAtDate.getTime())
        ? createdAtDate.toISOString()
        : new Date(createdAtValue).toISOString();
    const rawPrompt = entry.prompt || '';
    const description = typeof entry.description === 'string' ? entry.description.trim() : '';
    const inferredCharacterSwap =
      entry.taskType === 'character_swap'
      || /^Replace the subject in Image 1 with\b/i.test(rawPrompt);

    return {
      id:
        resolvedId,
      imageNumber: Number.isFinite(entry.imageNumber) ? entry.imageNumber : null,
      prompt: inferredCharacterSwap ? 'Character Swap' : rawPrompt,
      description: inferredCharacterSwap ? '' : description,
      createdAt: createdAtISO,
      createdAtValue,
      status: entry.status || 'completed',
      image: entry.image || null,
      sourceImages: Array.isArray(entry.sourceImages)
        ? entry.sourceImages.filter((item) => item && item.url)
        : [],
      model: entry.model || null,
      size: entry.size || null,
      taskType: inferredCharacterSwap ? 'character_swap' : (entry.taskType || 'generate'),
      tags: Array.isArray(entry.tags)
        ? entry.tags.filter((tag) => typeof tag === 'string' && tag.trim())
        : [],
    };
  }, []);

  const upsertHistoryEntry = useCallback(
    (entry) => {
      const normalized = normalizeHistoryEntry(entry);
      if (!normalized) {
        return;
      }

      setPromptHistory((previousHistory) => {
        const filtered = normalized.id
          ? previousHistory.filter((item) => item.id !== normalized.id)
          : previousHistory;
        return [normalized, ...filtered];
      });
    },
    [normalizeHistoryEntry],
  );

  const performSubscriptionCheck = useCallback(async (options = {}) => {
    const showLoading = options.showLoading !== false;

    if (!currentUserId) {
      setSubscription(null);
      setSubscriptionError(null);
      setIsSubscriptionLoading(false);
      return;
    }

    if (showLoading) {
      setIsSubscriptionLoading(true);
      setSubscriptionError(null);
    }

    try {
      const response = await accountApi.fetchProfile();
      if (!isMountedRef.current) {
        return;
      }
      const payload = response && response.data ? response.data : {};
      const normalizedSubscription = payload.subscription
        ? mapSubscriptionFromApi(payload.subscription)
        : null;
      setSubscription(normalizedSubscription);
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      if (!showLoading) {
        return;
      }
      const responseMessage =
        (error &&
          error.response &&
          error.response.data &&
          error.response.data.message) ||
        'Unable to verify credit plan status. Please try again.';
      setSubscription(null);
      setSubscriptionError(responseMessage);
    } finally {
      if (showLoading && isMountedRef.current) {
        setIsSubscriptionLoading(false);
      }
    }
  }, [currentUserId]);

  useEffect(() => {
    performSubscriptionCheck({ showLoading: true });
  }, [performSubscriptionCheck]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        performSubscriptionCheck({ showLoading: false });
      }
    };

    const refreshOnFocus = () => {
      performSubscriptionCheck({ showLoading: false });
    };

    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [performSubscriptionCheck]);

  const handleRetrySubscriptionCheck = useCallback(() => {
    if (isSubscriptionLoading) {
      return;
    }
    performSubscriptionCheck();
  }, [isSubscriptionLoading, performSubscriptionCheck]);

  const loadCharacterList = useCallback(async () => {
    setIsCharacterListLoading(true);
    setCharacterListError(null);
    try {
      const { data } = await characterApi.listCharacters();
      const items = Array.isArray(data && data.items) ? data.items : [];
      setCharacterList(items);
      return items;
    } catch (error) {
      const nextMessage =
        (error && error.response && error.response.data && error.response.data.message) ||
        'Unable to load characters.';
      setCharacterListError(nextMessage);
      return [];
    } finally {
      setIsCharacterListLoading(false);
    }
  }, []);

  const loadCharacterDetail = useCallback(async (characterId) => {
    if (!characterId) {
      return null;
    }

    setCharacterReviewLoading(true);
    try {
      const { data } = await characterApi.fetchCharacter(characterId);
      return data || null;
    } catch (error) {
      const nextMessage =
        (error && error.response && error.response.data && error.response.data.message) ||
        'Unable to load character details.';
      message.error(nextMessage);
      return null;
    } finally {
      setCharacterReviewLoading(false);
    }
  }, []);

  const handleOpenCharacterPicker = useCallback(async (mode = 'swap') => {
    setCharacterPickerMode(mode === 'reference' ? 'reference' : 'swap');
    setCharacterPickerOpen(true);
    if (!characterList.length && !isCharacterListLoading) {
      await loadCharacterList();
    }
  }, [characterList.length, isCharacterListLoading, loadCharacterList]);

  const handleCreateCharacterFromGenerator = useCallback(() => {
    setCharacterPickerOpen(false);
    history.push('/character/create');
  }, [history]);

  const handleClearCharacterSelection = useCallback(() => {
    setSelectedCharacter(null);
    setActivateCharacterSwap(false);
  }, []);

  const handleSelectCharacter = useCallback(async (characterId) => {
    const detail = await loadCharacterDetail(characterId);
    if (!detail) {
      return;
    }
    setSelectedCharacter(detail);
    setActivateCharacterSwap(characterPickerMode === 'swap');
    setCharacterPickerOpen(false);
    message.success(characterPickerMode === 'swap'
      ? 'Reference character selected.'
      : 'Character reference selected.');
  }, [characterPickerMode, loadCharacterDetail]);

  const findRecoveredHistoryEntry = useCallback(
    (items, criteria = {}) => {
      if (!Array.isArray(items) || !items.length) {
        return null;
      }

      const minCreatedAt =
        criteria.startedAt instanceof Date && !Number.isNaN(criteria.startedAt.getTime())
          ? criteria.startedAt.getTime() - HISTORY_RECOVERY_MATCH_WINDOW_MS
          : Date.now() - HISTORY_RECOVERY_MATCH_WINDOW_MS;
      const expectedPrompt = normalizeComparableText(criteria.prompt);
      const expectedModel = normalizeComparableText(criteria.model);
      const expectedSize = normalizeComparableText(criteria.size);
      const expectedTaskType = normalizeComparableText(criteria.taskType || 'generate');

      return items
        .map((item) => normalizeHistoryEntry(item))
        .filter(Boolean)
        .find((entry) => {
          if (!entry.image || !entry.image.url || entry.status !== 'completed') {
            return false;
          }
          if (entry.createdAtValue < minCreatedAt) {
            return false;
          }
          if (
            expectedTaskType &&
            normalizeComparableText(entry.taskType || 'generate') !== expectedTaskType
          ) {
            return false;
          }
          if (expectedModel && normalizeComparableText(entry.model) !== expectedModel) {
            return false;
          }
          if (expectedSize && normalizeComparableText(entry.size) !== expectedSize) {
            return false;
          }
          if (expectedPrompt && normalizeComparableText(entry.prompt) !== expectedPrompt) {
            return false;
          }
          return true;
        }) || null;
    },
    [normalizeHistoryEntry],
  );

  const recoverCompletedGenerationFromHistory = useCallback(
    async (criteria = {}) => {
      for (let attempt = 0; attempt < HISTORY_RECOVERY_ATTEMPTS; attempt += 1) {
        if (!isMountedRef.current) {
          return null;
        }

        if (attempt > 0) {
          await wait(HISTORY_RECOVERY_DELAY_MS);
        }

        try {
          const { data } = await aiApi.fetchHistory({
            limit: HISTORY_PAGE_SIZE,
            offset: 0,
            search: criteria.prompt || undefined,
            sort: 'newest',
          });
          const items = Array.isArray(data && data.items) ? data.items : [];
          const recoveredEntry = findRecoveredHistoryEntry(items, criteria);
          if (recoveredEntry) {
            upsertHistoryEntry(recoveredEntry);
            setLatestGeneration({
              id: recoveredEntry.id,
              prompt: recoveredEntry.prompt,
              image: recoveredEntry.image,
              model: recoveredEntry.model || criteria.model || null,
              size: recoveredEntry.size || criteria.size || null,
              taskType: recoveredEntry.taskType || criteria.taskType || 'generate',
            });
            setGenerationError(null);
            return recoveredEntry;
          }
        } catch (historyRecoveryError) {
          // Keep the original generation error path; recovery is best-effort.
        }
      }

      return null;
    },
    [findRecoveredHistoryEntry, upsertHistoryEntry],
  );

  const waitForGenerationJob = useCallback(
    async (requestId, fallback = {}) => {
      if (!requestId) {
        return null;
      }

      for (let attempt = 0; attempt < GENERATION_STATUS_MAX_POLLS; attempt += 1) {
        if (!isMountedRef.current) {
          return null;
        }

        if (attempt > 0) {
          const delay =
            attempt <= GENERATION_STATUS_FAST_POLL_ATTEMPTS
              ? GENERATION_STATUS_FAST_POLL_MS
              : GENERATION_STATUS_SLOW_POLL_MS;
          await wait(delay);
        }

        const { data } = await aiApi.fetchHistoryEntry(requestId);
        const status = String((data && data.status) || '').toLowerCase();

        if (status === 'completed') {
          const normalizedEntry = normalizeHistoryEntry(data);
          if (!normalizedEntry || !normalizedEntry.image || !normalizedEntry.image.url) {
            throw new Error('Image URL missing from completed generation.');
          }

          upsertHistoryEntry(normalizedEntry);
          setLatestGeneration({
            id: normalizedEntry.id,
            prompt: normalizedEntry.prompt || fallback.prompt || '',
            image: normalizedEntry.image,
            model: normalizedEntry.model || fallback.model || null,
            size: normalizedEntry.size || fallback.size || null,
            taskType: normalizedEntry.taskType || fallback.taskType || 'generate',
          });
          setGenerationError(null);
          return normalizedEntry;
        }

        if (status === 'failed') {
          throw new Error(
            (data && data.errorMessage)
              || (data && data.errorCode)
              || 'Generation was not completed. Please review the prompt, model, or reference inputs and try again.',
          );
        }
      }

      throw new Error('Generation is still processing. Please check History again shortly.');
    },
    [normalizeHistoryEntry, upsertHistoryEntry],
  );

  useEffect(() => {
    if (!currentUserId) {
      return undefined;
    }

    const pendingJobs = readPendingGenerationJobs(currentUserId);
    if (!pendingJobs.length) {
      return undefined;
    }

    const latestJob = pendingJobs[pendingJobs.length - 1];
    if (!latestJob || pendingPollsRef.current.has(latestJob.requestId)) {
      return undefined;
    }

    let cancelled = false;
    pendingPollsRef.current.add(latestJob.requestId);
    setIsGenerating(true);
    setGenerationError(null);

    waitForGenerationJob(latestJob.requestId, latestJob)
      .then((completedEntry) => {
        if (cancelled) {
          return;
        }
        if (completedEntry) {
          removePendingGenerationJob(currentUserId, latestJob.requestId);
          message.success('Image generated successfully');
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        const responseMessage = sanitizeUiErrorMessage(
          extractApiErrorMessage(
            error,
            'Generation was not completed. Please review the prompt, model, or reference inputs and try again.',
          ),
          'Generation was not completed. Please review the prompt, model, or reference inputs and try again.',
        );
        setGenerationError(responseMessage);
        showGenerationErrorNotification(
          getGenerationErrorTitle(responseMessage, 'Image generation failed'),
          responseMessage,
        );
        if (!String(responseMessage || '').toLowerCase().includes('still processing')) {
          removePendingGenerationJob(currentUserId, latestJob.requestId);
        }
      })
      .finally(() => {
        pendingPollsRef.current.delete(latestJob.requestId);
        if (!cancelled) {
          setIsGenerating(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentUserId, waitForGenerationJob]);

  const handlePromptGenerated = useCallback(
    async (generationInput) => {
      const requestPrompt =
        typeof generationInput === 'string'
          ? generationInput
          : generationInput && generationInput.prompt
          ? generationInput.prompt
          : '';
      const displayPrompt =
        generationInput && typeof generationInput === 'object' && generationInput.displayPrompt
          ? generationInput.displayPrompt
          : requestPrompt;

      const trimmedRequestPrompt = (requestPrompt || '').trim();
      const trimmedDisplayPrompt = (displayPrompt || '').trim();

      if (!trimmedRequestPrompt || isGenerating) {
        return;
      }

      const requestedModel =
        generationInput && typeof generationInput === 'object' && generationInput.model
          ? generationInput.model
          : selectedModel;
      const requestedSize =
        generationInput && typeof generationInput === 'object' && generationInput.size
          ? generationInput.size
          : selectedSize;
      const requestedTaskType =
        generationInput && typeof generationInput === 'object' && generationInput.taskType
          ? generationInput.taskType
          : null;
      const requestedHistoryPrompt =
        generationInput && typeof generationInput === 'object' && generationInput.historyPrompt
          ? generationInput.historyPrompt
          : null;
      const imageFiles =
        generationInput && typeof generationInput === 'object' && generationInput.imageFiles
          ? generationInput.imageFiles
          : [];
      const imageUrls =
        generationInput && typeof generationInput === 'object' && generationInput.imageUrls
          ? generationInput.imageUrls
          : [];
      const imageFile = imageFiles.length ? imageFiles[0] : null;
      const maskFile =
        generationInput && typeof generationInput === 'object' && generationInput.maskFile
          ? generationInput.maskFile
          : null;

      const useImageReference =
        generationInput &&
        typeof generationInput === 'object' &&
        generationInput.useImageReference;
      if (requestedModel === 'qwen' || useImageReference) {
        await handleEditRequest({
          prompt: trimmedRequestPrompt,
          displayPrompt: trimmedDisplayPrompt,
          historyPrompt: requestedHistoryPrompt,
          imageFile,
          imageFiles,
          imageUrls,
          maskFile,
          size: requestedSize,
          model: requestedModel,
          taskType: requestedTaskType,
        });
        return;
      }

      setIsGenerating(true);
      setGenerationError(null);
      const promptForHistory = trimmedDisplayPrompt || trimmedRequestPrompt;
      const requestStartedAt = new Date();

      try {
        const { data } = await aiApi.imagine({
          prompt: trimmedRequestPrompt,
          model: requestedModel,
          size: requestedSize,
          async: true,
        });
        const responseData = data || {};
        if (responseData.status === 'pending' && responseData.requestId) {
          const pendingJob = {
            requestId: responseData.requestId,
            userId: currentUserId,
            prompt: promptForHistory,
            model: requestedModel || null,
            size: requestedSize || null,
            taskType: 'generate',
          };
          savePendingGenerationJob(pendingJob);
          pendingPollsRef.current.add(responseData.requestId);
          try {
            const completedEntry = await waitForGenerationJob(responseData.requestId, pendingJob);
            if (completedEntry) {
              removePendingGenerationJob(currentUserId, responseData.requestId);
              message.success('Image generated successfully');
            }
          } catch (pollError) {
            if (!String((pollError && pollError.message) || '').toLowerCase().includes('still processing')) {
              removePendingGenerationJob(currentUserId, responseData.requestId);
            }
            throw pollError;
          } finally {
            pendingPollsRef.current.delete(responseData.requestId);
          }
          return;
        }
        const imageData = responseData.image || null;

        const historyEntry =
          responseData && responseData.historyEntry
            ? {
                ...responseData.historyEntry,
                prompt:
                  responseData.historyEntry.prompt || promptForHistory,
                image: responseData.historyEntry.image || imageData,
                model:
                  responseData.historyEntry.model ||
                  responseData.model ||
                  requestedModel ||
                  null,
                size:
                  responseData.historyEntry.size ||
                  responseData.size ||
                  requestedSize ||
                  null,
              }
            : null;
        const normalizedEntry =
          historyEntry ||
          {
            id: `local-${Date.now()}`,
            prompt: promptForHistory,
            createdAt: new Date().toISOString(),
            image: imageData,
            status: 'completed',
            model: responseData.model || requestedModel || null,
            size: responseData.size || requestedSize || null,
          };

        if (!normalizedEntry.image || !normalizedEntry.image.url) {
          throw new Error('Image URL missing from response');
        }

        setLatestGeneration({
          id: normalizedEntry.id,
          prompt: normalizedEntry.prompt,
          image: normalizedEntry.image,
          model: responseData.model || requestedModel || null,
          size: responseData.size || requestedSize || null,
        });

        upsertHistoryEntry(normalizedEntry);

        if (responseData.quota) {
          setSubscription((previous) => {
            if (!previous) {
              return previous;
            }
            const nextLimit =
              responseData.quota.limit != null
                ? responseData.quota.limit
                : previous.quotaLimit;
            const nextUsed =
              responseData.quota.used != null
                ? responseData.quota.used
                : previous.quotaUsed;
            const nextRemaining =
              responseData.quota.remaining != null
                ? responseData.quota.remaining
                : nextLimit != null
                ? Math.max(nextLimit - nextUsed, 0)
                : previous.quotaRemaining;

            return {
              ...previous,
              quotaLimit: nextLimit,
              quotaUsed: nextUsed,
              quotaRemaining: nextRemaining,
              isQuotaExhausted:
                nextLimit != null ? nextRemaining <= 0 : previous.isQuotaExhausted,
            };
          });
        }
        message.success('Image generated successfully');
      } catch (error) {
        const recoveredEntry = await recoverCompletedGenerationFromHistory({
          startedAt: requestStartedAt,
          prompt: promptForHistory,
          model: requestedModel,
          size: requestedSize,
          taskType: 'generate',
        });
        if (recoveredEntry) {
          message.success('Image completed after a short delay');
          return;
        }

        const responseMessage = sanitizeUiErrorMessage(extractApiErrorMessage(
          error,
          'Failed to generate image. Please review the prompt and try again.',
        ), 'Failed to generate image. Please review the prompt and try again.');
        setGenerationError(responseMessage);
        showGenerationErrorNotification(getGenerationErrorTitle(responseMessage, 'Image generation failed'), responseMessage);
      } finally {
        setIsGenerating(false);
      }
    },
    [
      currentUserId,
      isGenerating,
      recoverCompletedGenerationFromHistory,
      selectedModel,
      selectedSize,
      upsertHistoryEntry,
      waitForGenerationJob,
    ],
  );

  const handleEditRequest = useCallback(
    async ({
      prompt: promptText,
      displayPrompt,
      historyPrompt,
      imageFile,
      imageFiles,
      imageUrls,
      maskFile,
      size: requestedSize,
      model,
      taskType,
    }) => {
      const trimmedPrompt = (promptText || '').trim();
      const trimmedDisplayPrompt = (displayPrompt || promptText || '').trim();
      if (!trimmedPrompt || isGenerating) {
        return;
      }
      const availableImages = imageFiles && imageFiles.length ? imageFiles : (imageFile ? [imageFile] : []);
      const availableImageUrls = Array.isArray(imageUrls)
        ? imageUrls.filter(Boolean)
        : imageUrls
        ? [imageUrls]
        : [];
      if (!availableImages.length && !availableImageUrls.length) {
        message.error('Please add a source image to edit.');
        return;
      }

      const resolvedSize = requestedSize || selectedSize;
      setIsGenerating(true);
      setGenerationError(null);
      const requestedModel = model || selectedModel;
      const promptFromResponse = trimmedDisplayPrompt || trimmedPrompt;
      const promptForHistory = historyPrompt || promptFromResponse;
      const resolvedTaskType = taskType || 'edit';
      const requestStartedAt = new Date();

      try {
        const { data } = await aiApi.edit({
          prompt: trimmedPrompt,
          image: availableImages[0],
          images: availableImages,
          imageUrls: availableImageUrls,
          mask: maskFile,
          size: resolvedSize,
          model: requestedModel,
          taskType: resolvedTaskType,
          historyPrompt: promptForHistory,
          async: true,
        });
        const responseData = data || {};
        if (responseData.status === 'pending' && responseData.requestId) {
          const pendingJob = {
            requestId: responseData.requestId,
            userId: currentUserId,
            prompt: promptForHistory,
            model: requestedModel || null,
            size: resolvedSize || null,
            taskType: resolvedTaskType,
          };
          savePendingGenerationJob(pendingJob);
          pendingPollsRef.current.add(responseData.requestId);
          try {
            const completedEntry = await waitForGenerationJob(responseData.requestId, pendingJob);
            if (completedEntry) {
              removePendingGenerationJob(currentUserId, responseData.requestId);
              message.success('Edit completed');
            }
          } catch (pollError) {
            if (!String((pollError && pollError.message) || '').toLowerCase().includes('still processing')) {
              removePendingGenerationJob(currentUserId, responseData.requestId);
            }
            throw pollError;
          } finally {
            pendingPollsRef.current.delete(responseData.requestId);
          }
          return;
        }
        const imageData = responseData.image || null;

        const historyEntry =
          responseData && responseData.historyEntry
            ? {
                ...responseData.historyEntry,
                prompt: promptForHistory,
                image: responseData.historyEntry.image || imageData,
                taskType: responseData.historyEntry.taskType || resolvedTaskType,
              }
            : null;

        const normalizedEntry =
          historyEntry ||
          {
            id: `local-${Date.now()}`,
            prompt: promptForHistory,
            createdAt: new Date().toISOString(),
            image: imageData,
            status: 'completed',
            model: responseData.model || requestedModel,
            size: responseData.size || resolvedSize,
            taskType: resolvedTaskType,
          };

        if (!normalizedEntry.image || !normalizedEntry.image.url) {
          throw new Error('Image URL missing from response');
        }

        setLatestGeneration({
          id: normalizedEntry.id,
          prompt: promptForHistory,
          image: normalizedEntry.image,
          model: normalizedEntry.model || responseData.model || requestedModel,
          size: normalizedEntry.size || resolvedSize,
          taskType: normalizedEntry.taskType || resolvedTaskType,
        });

        upsertHistoryEntry(normalizedEntry);

        if (responseData.quota) {
          setSubscription((previous) => {
            if (!previous) {
              return previous;
            }
            const nextLimit =
              responseData.quota.limit != null
                ? responseData.quota.limit
                : previous.quotaLimit;
            const nextUsed =
              responseData.quota.used != null
                ? responseData.quota.used
                : previous.quotaUsed;
            const nextRemaining =
              responseData.quota.remaining != null
                ? responseData.quota.remaining
                : nextLimit != null
                ? Math.max(nextLimit - nextUsed, 0)
                : previous.quotaRemaining;

            return {
              ...previous,
              quotaLimit: nextLimit,
              quotaUsed: nextUsed,
              quotaRemaining: nextRemaining,
              isQuotaExhausted:
                nextLimit != null ? nextRemaining <= 0 : previous.isQuotaExhausted,
            };
          });
        }

        message.success('Edit completed');
      } catch (error) {
        const recoveredEntry = await recoverCompletedGenerationFromHistory({
          startedAt: requestStartedAt,
          prompt: promptForHistory,
          model: requestedModel,
          size: resolvedSize,
          taskType: resolvedTaskType,
        });
        if (recoveredEntry) {
          message.success('Image completed after a short delay');
          return;
        }

        const responseMessage = sanitizeUiErrorMessage(extractApiErrorMessage(
          error,
          'Failed to edit image. Please review the prompt and try again.',
        ), 'Failed to edit image. Please review the prompt and try again.');
        setGenerationError(responseMessage);
        showGenerationErrorNotification(getGenerationErrorTitle(responseMessage, 'Image edit failed'), responseMessage);
      } finally {
        setIsGenerating(false);
      }
    },
    [
      currentUserId,
      isGenerating,
      recoverCompletedGenerationFromHistory,
      selectedModel,
      selectedSize,
      upsertHistoryEntry,
      waitForGenerationJob,
    ],
  );

  useEffect(() => {
    if (!location.state) {
      return;
    }

    const {
      prompt: promptFromState,
      referenceImageUrls: referenceImageUrlsFromState,
      selectedCharacterId,
    } = location.state;

    if (promptFromState) {
      setCurrentPrompt(promptFromState);
      setPromptSelectionVersion((current) => current + 1);
      setLatestGeneration(null);
      setGenerationError(null);
      setHistoryReferenceImages(
        Array.isArray(referenceImageUrlsFromState)
          ? referenceImageUrlsFromState.filter(Boolean).slice(0, 3)
          : [],
      );
    }

    if (selectedCharacterId) {
      loadCharacterDetail(selectedCharacterId).then((detail) => {
        if (detail) {
          setSelectedCharacter(detail);
          setActivateCharacterSwap(true);
          setSelectedModel(DEFAULT_GENERATION_MODEL);
        }
      });
    }

    history.replace({ ...location, state: undefined });
  }, [history, loadCharacterDetail, location]);

  const handlePromptChange = useCallback((nextPrompt) => {
    setCurrentPrompt(nextPrompt);
  }, []);

  const activeReferenceImageUrls = useMemo(() => {
    return Array.isArray(historyReferenceImages)
      ? historyReferenceImages.filter(Boolean).slice(0, 3)
      : [];
  }, [historyReferenceImages]);

  const generatorWorkspace = useMemo(
    () => (
      <div className="workspace">
        <GeneratePanel
          promptValue={currentPrompt}
          promptVersion={promptSelectionVersion}
          onPromptChange={handlePromptChange}
          onGenerate={handlePromptGenerated}
          isGenerating={isGenerating}
          modelValue={selectedModel}
          onModelChange={setSelectedModel}
          sizeValue={selectedSize}
          onSizeChange={setSelectedSize}
          referenceImageUrls={activeReferenceImageUrls}
          selectedCharacter={selectedCharacter}
          activateCharacterSwap={activateCharacterSwap}
          onOpenCharacterPicker={handleOpenCharacterPicker}
          onCreateCharacter={handleCreateCharacterFromGenerator}
          onClearCharacterSelection={handleClearCharacterSelection}
          generationModels={generationModels}
        />
        <PreviewPanel
          isLoading={isGenerating}
          generation={latestGeneration}
          errorMessage={generationError}
        />
      </div>
    ),
    [
      currentPrompt,
      promptSelectionVersion,
      handlePromptChange,
      handlePromptGenerated,
      handleOpenCharacterPicker,
      handleCreateCharacterFromGenerator,
      handleClearCharacterSelection,
      generationError,
      isGenerating,
      latestGeneration,
      selectedModel,
      selectedSize,
      generationModels,
      activeReferenceImageUrls,
      selectedCharacter,
      activateCharacterSwap,
    ],
  );

  let pageBody = null;
  if (isSubscriptionLoading) {
    pageBody = (
      <div className="ai-access-gate ai-access-gate--loading" role="status" aria-live="polite">
        <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
        <h2>Checking your credit plan…</h2>
        <p>Hang tight while we confirm your access.</p>
      </div>
    );
  } else if (subscription && subscription.isQuotaExhausted) {
    pageBody = (
      <div className="ai-access-gate ai-access-gate--warning">
        <div className="ai-access-gate__icon" aria-hidden="true">
          <i className="fa-solid fa-gauge-high" />
        </div>
        <h2>You&apos;ve reached your credit limit</h2>
        <p>
          {quotaLimit != null
            ? `You have used ${quotaUsed.toLocaleString()} of ${quotaLimit.toLocaleString()} credits for this plan.`
            : 'You have used all generation credits included in this plan.'}
        </p>
        <div className="ai-access-gate__actions">
          <Button type="primary" size="large" onClick={() => history.push('/account')}>
            Renew plan
          </Button>
          <Button size="large" onClick={handleRetrySubscriptionCheck} loading={isSubscriptionLoading}>
            Refresh status
          </Button>
        </div>
      </div>
    );
  } else if (!subscription) {
    pageBody = (
      <div className="ai-access-gate">
        <div className="ai-access-gate__icon" aria-hidden="true">
          <i className="fa-solid fa-lock" />
        </div>
        <h2>Activate a credit plan to start using AI</h2>
        <p>Activate a plan to generate new images and unlock your prompt history.</p>
        <div className="ai-access-gate__actions">
          <Button type="primary" size="large" onClick={() => history.push('/account')}>
            Activate credit plan
          </Button>
          <Button size="large" onClick={handleRetrySubscriptionCheck} loading={isSubscriptionLoading}>
            Retry check
          </Button>
        </div>
        {subscriptionError ? (
          <p className="ai-access-gate__error" role="alert">
            {subscriptionError}
          </p>
        ) : (
          <p className="ai-access-gate__footnote">Already upgraded? Retry or refresh the page.</p>
        )}
      </div>
    );
  } else {
    pageBody = (
      <>
        {quotaLimit != null && (
          <div
            className={`ai-quota-pill${
              quotaRemainingPercent != null && quotaRemainingPercent <= 5
                ? ' ai-quota-pill--danger'
                : quotaRemainingPercent != null && quotaRemainingPercent <= 25
                ? ' ai-quota-pill--warning'
                : ''
            }`}
          >
            <div className="ai-quota-pill__row">
              <span className="ai-quota-pill__label">Credits remaining</span>
              <span className="ai-quota-pill__value">
                {quotaRemaining != null ? quotaRemaining.toLocaleString() : '∞'}{' '}
                {quotaLimit != null ? `/ ${quotaLimit.toLocaleString()}` : ''}
              </span>
            </div>
            <div className="ai-quota-pill__meter" aria-hidden="true">
              <span style={{ width: `${quotaRemainingPercent || 0}%` }} />
            </div>
          </div>
        )}
        {generatorWorkspace}
        <Modal
          open={characterPickerOpen}
          title="Select Reference Character"
          onCancel={() => setCharacterPickerOpen(false)}
          footer={null}
          width={920}
        >
          {isCharacterListLoading ? (
            <div className="character-picker-modal__empty">
              <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
            </div>
          ) : characterListError ? (
            <div className="character-picker-modal__empty">
              <p>{characterListError}</p>
            </div>
          ) : characterList.filter((character) => character.swapStatus !== 'blocked').length ? (
            <div className="character-picker-modal__grid">
              <button
                type="button"
                className="character-picker-card character-picker-card--create"
                onClick={handleCreateCharacterFromGenerator}
              >
                <div className="character-picker-card__image">
                  <i className="fa-solid fa-plus" aria-hidden="true" />
                </div>
              </button>
              {characterList
                .filter((character) => character.swapStatus !== 'blocked')
                .map((character) => {
                  const previewAsset = getCharacterPreviewAsset(character);
                  return (
                    <button
                      key={character.id}
                      type="button"
                      className={`character-picker-card${
                        selectedCharacter && selectedCharacter.id === character.id
                          ? ' character-picker-card--active'
                          : ''
                      }`}
                      onClick={() => handleSelectCharacter(character.id)}
                    >
                      <div className="character-picker-card__image">
                        {previewAsset ? (
                          <img src={previewAsset.url} alt={character.displayName} />
                        ) : (
                          <i className="fa-regular fa-user" aria-hidden="true" />
                        )}
                      </div>
                    </button>
                  );
                })}
            </div>
          ) : (
            <div className="character-picker-modal__grid">
              <button
                type="button"
                className="character-picker-card character-picker-card--create"
                onClick={handleCreateCharacterFromGenerator}
              >
                <div className="character-picker-card__image">
                  <i className="fa-solid fa-plus" aria-hidden="true" />
                </div>
              </button>
            </div>
          )}
        </Modal>
        <Modal
          open={characterReviewOpen}
          title={selectedCharacter ? selectedCharacter.displayName : 'Character Review'}
          onCancel={() => setCharacterReviewOpen(false)}
          footer={null}
          width={980}
        >
          {characterReviewLoading ? (
            <div className="character-picker-modal__empty">
              <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
            </div>
          ) : selectedCharacter ? (
            <div className="character-review-modal">
              <section className="character-review-modal__section">
                <h3>Uploaded Images</h3>
                <div className="character-picker-modal__grid character-picker-modal__grid--detail">
                  {(selectedCharacter.uploadedSourceImages || []).map((image) => (
                    <article key={image.id || image.url} className="character-review-tile">
                      <img src={image.url} alt={image.originalName || 'Uploaded source'} />
                    </article>
                  ))}
                </div>
              </section>
              <section className="character-review-modal__section">
                <h3>Character ID</h3>
                <div className="character-picker-modal__grid character-picker-modal__grid--detail">
                  {selectedCharacter.coverAvatar && selectedCharacter.coverAvatar.url ? (
                    <article
                      key={selectedCharacter.coverAvatar.id || selectedCharacter.coverAvatar.url}
                      className="character-review-tile"
                    >
                      <img
                        src={selectedCharacter.coverAvatar.url}
                        alt={selectedCharacter.coverAvatar.originalName || 'Character ID'}
                      />
                    </article>
                  ) : null}
                </div>
              </section>
            </div>
          ) : null}
        </Modal>
      </>
    );
  }

  return (
    <section className={`ai-page${isGenerating ? ' ai-page--busy' : ''}`}>
      <header className="page-header">
        <h1>
          <span className="eyebrow">AI Image Generator:</span>
          <span className="headline">Instantly create stunning images</span>
        </h1>
      </header>
      {pageBody}
      {isGenerating ? (
        <div className="ai-page__blocking-overlay" role="alert" aria-live="polite">
          <div className="ai-page__blocking-card">
            <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
            <h3>Generating Image</h3>
            <p>Please wait while we process your request.</p>
          </div>
        </div>
      ) : null}
    </section>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

export default withRouter(connect(mapStateToProps)(AI));
